import { useState } from 'react';
import {
  Table, Button, Space, Tag, Modal, Form, Input, Select, message,
  Popconfirm, Drawer, Timeline, Badge, Row, Col, Alert
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SyncOutlined,
  HistoryOutlined, TableOutlined, EyeOutlined, CodeOutlined,
  ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
  MinusCircleOutlined, ImportOutlined, CloudSyncOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  dataSourcesApi,
  templateMirrorApi,
  type CreateDataSourceRequest,
  type UpdateDataSourceRequest,
  type SyncHistory,
  type ParameterDefinition,
  type BulkImportItem,
  type BulkImportResult,
} from '../../api/dataSources';
import { normalizeImportItem } from '../../utils/normalizeImportItem';
import { AGENT_BACKEND } from '../../types';
import type { AgentBackend, DataSource } from '../../types';

const sourceTypeOptions = [
  { value: 'table', label: 'Tablo', icon: <TableOutlined /> },
  { value: 'view', label: 'View', icon: <EyeOutlined /> },
  { value: 'sp', label: 'Stored Procedure', icon: <CodeOutlined /> },
];

const schedulePresets = [
  { value: '300', label: 'Her 5 dakika' },
  { value: '900', label: 'Her 15 dakika' },
  { value: '1800', label: 'Her 30 dakika' },
  { value: '3600', label: 'Saatte bir' },
  { value: '86400', label: 'Günde bir' },
];

export function FirmDataSourcesPanel({ firmId, agentBackend }: { firmId: number; agentBackend?: AgentBackend }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<DataSource | null>(null);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importItems, setImportItems] = useState<BulkImportItem[] | null>(null);
  const [importFileName, setImportFileName] = useState<string | null>(null);
  const [importParseError, setImportParseError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<BulkImportResult | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const isIttihatMirror = agentBackend === AGENT_BACKEND.IttihatAgent;

  const { data: sources = [], isLoading } = useQuery({
    queryKey: ['data-sources', firmId],
    queryFn: () => firmId ? dataSourcesApi.getByFirm(firmId) : Promise.resolve([]),
    enabled: !!firmId,
  });

  const { data: syncHistory = [], isLoading: historyLoading } = useQuery({
    queryKey: ['sync-history', firmId, selectedSourceId],
    queryFn: () => firmId && selectedSourceId
      ? dataSourcesApi.getSyncHistory(firmId, selectedSourceId)
      : Promise.resolve([]),
    enabled: !!firmId && !!selectedSourceId,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateDataSourceRequest) =>
      dataSourcesApi.create(firmId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-sources'] });
      message.success('Veri kaynağı oluşturuldu');
      setIsModalOpen(false);
      form.resetFields();
    },
    onError: () => message.error('Veri kaynağı oluşturulamadı'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateDataSourceRequest }) =>
      dataSourcesApi.update(firmId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-sources'] });
      message.success('Veri kaynağı güncellendi');
      setIsModalOpen(false);
      setEditingSource(null);
      form.resetFields();
    },
    onError: () => message.error('Veri kaynağı güncellenemedi'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => dataSourcesApi.delete(firmId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-sources'] });
      message.success('Veri kaynağı silindi');
    },
    onError: () => message.error('Veri kaynağı silinemedi'),
  });

  const syncMutation = useMutation({
    mutationFn: (id: number) => dataSourcesApi.triggerSync(firmId, id),
    onSuccess: (data) => {
      message.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['data-sources'] });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      message.error(error.response?.data?.message || 'Sync başlatılamadı');
    },
  });

  const forceSyncMutation = useMutation({
    mutationFn: () => templateMirrorApi.forceSync(firmId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['data-sources'] });
      if (result.status === 'Synced') {
        message.success(
          `Hub mirror güncellendi: ${result.templatesUpserted} eklendi/güncellendi, ${result.orphansDemoted} orphan pasif`,
        );
      } else if (result.status === 'NotModified') {
        message.info('Hub değişmemiş — mevcut mirror taze (304)');
      } else {
        message.warning(`Sync sonucu: ${result.status}${result.error ? ` — ${result.error}` : ''}`);
      }
    },
    onError: (error: Error & { response?: { data?: { error?: string } } }) => {
      message.error(error.response?.data?.error || 'Hub mirror sync başarısız');
    },
  });

  const importMutation = useMutation({
    mutationFn: (items: BulkImportItem[]) =>
      dataSourcesApi.bulkImport(firmId, items),
    onSuccess: (result) => {
      setImportResult(result);
      queryClient.invalidateQueries({ queryKey: ['data-sources'] });
      const summary = `${result.imported} yeni, ${result.updated} güncellendi`;
      if (result.skipped > 0) {
        message.warning(`${summary}, ${result.skipped} atlandı`);
      } else {
        message.success(summary);
      }
    },
    onError: () => message.error('İçe aktarım başarısız'),
  });

  const closeImportModal = () => {
    setImportModalOpen(false);
    setImportItems(null);
    setImportFileName(null);
    setImportParseError(null);
    setImportResult(null);
  };

  const handleImportFile = async (file: File) => {
    setImportParseError(null);
    setImportItems(null);
    setImportResult(null);
    setImportFileName(file.name);

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const rawItems = Array.isArray(parsed) ? parsed : parsed?.items;
      if (!Array.isArray(rawItems)) {
        throw new Error('JSON kök öğesi dizi veya { "items": [...] } olmalı');
      }
      const items: BulkImportItem[] = rawItems.map((item: Record<string, unknown>, idx: number) =>
        normalizeImportItem(item, idx),
      );
      if (items.length === 0) {
        throw new Error('Boş dizi');
      }
      setImportItems(items);
    } catch (e) {
      setImportParseError((e as Error).message);
    }
  };

  const handleSubmit = (values: CreateDataSourceRequest & {
    scheduleInterval?: string;
    parameterList?: Array<{ name: string }>;
  }) => {
    const schedule = values.scheduleInterval ? {
      type: 'interval',
      value: values.scheduleInterval,
    } : undefined;

    // Convert parameter list to dictionary (simple: just names)
    let parameters: Record<string, ParameterDefinition> | undefined;
    if (values.parameterList && values.parameterList.length > 0) {
      parameters = {};
      for (const param of values.parameterList) {
        if (param.name) {
          parameters[param.name] = {
            type: 'string',
            required: false,
          };
        }
      }
    }

    if (editingSource) {
      const updateData: UpdateDataSourceRequest = {
        name: values.name,
        sourceType: values.sourceType,
        sourceName: values.sourceName,
        parameters,
        schedule,
      };
      updateMutation.mutate({ id: editingSource.id, data: updateData });
    } else {
      const createData: CreateDataSourceRequest = {
        name: values.name,
        sourceType: values.sourceType,
        sourceName: values.sourceName,
        parameters,
        schedule,
      };
      createMutation.mutate(createData);
    }
  };

  const handleEdit = (source: DataSource) => {
    setEditingSource(source);
    const schedule = source.schedule ? JSON.parse(source.schedule) : null;

    // Convert parameters dictionary to list (just names)
    let parameterList: Array<{ name: string }> = [];
    if (source.parameters) {
      try {
        const params = JSON.parse(source.parameters) as Record<string, ParameterDefinition>;
        parameterList = Object.keys(params).map(name => ({ name }));
      } catch {
        // Invalid JSON, ignore
      }
    }

    form.setFieldsValue({
      ...source,
      scheduleInterval: schedule?.value,
      parameterList,
    });
    setIsModalOpen(true);
  };

  const handleViewHistory = (sourceId: number) => {
    setSelectedSourceId(sourceId);
    setHistoryDrawerOpen(true);
  };

  const getSourceTypeIcon = (type: string) => {
    switch (type) {
      case 'table': return <TableOutlined />;
      case 'view': return <EyeOutlined />;
      case 'sp': return <CodeOutlined />;
      default: return <TableOutlined />;
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      render: (id: number) => <code style={{ fontWeight: 600 }}>{id}</code>,
    },
    {
      title: 'Kaynak Adı',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: DataSource) => (
        <Space>
          {getSourceTypeIcon(record.sourceType)}
          <span>{name}</span>
        </Space>
      ),
    },
    {
      title: 'Tip',
      dataIndex: 'sourceType',
      key: 'sourceType',
      render: (type: string) => {
        const option = sourceTypeOptions.find(o => o.value === type);
        return <Tag>{option?.label || type}</Tag>;
      },
    },
    {
      title: 'Kaynak',
      dataIndex: 'sourceName',
      key: 'sourceName',
      render: (name: string) => <code>{name}</code>,
    },
    {
      title: 'Zamanlama',
      dataIndex: 'schedule',
      key: 'schedule',
      render: (schedule: string | null) => {
        if (!schedule) return <Tag>Manuel</Tag>;
        const parsed = JSON.parse(schedule);
        const preset = schedulePresets.find(p => p.value === parsed.value);
        return (
          <Tag icon={<ClockCircleOutlined />} color="blue">
            {preset?.label || `${parsed.value}s`}
          </Tag>
        );
      },
    },
    {
      title: 'Son Sync',
      dataIndex: 'lastSyncAt',
      key: 'lastSyncAt',
      render: (date: string | null) =>
        date ? new Date(date).toLocaleString('tr-TR') : '-',
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Badge status={isActive ? 'success' : 'default'} text={isActive ? 'Aktif' : 'Pasif'} />
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: DataSource) => (
        <Space>
          <Button
            type="text"
            icon={<SyncOutlined spin={syncMutation.isPending} />}
            onClick={() => syncMutation.mutate(record.id)}
            title="Şimdi Sync Et"
          />
          <Button
            type="text"
            icon={<HistoryOutlined />}
            onClick={() => handleViewHistory(record.id)}
            title="Geçmiş"
          />
          {!isIttihatMirror && (
            <>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
              <Popconfirm
                title="Veri kaynağını silmek istediğinize emin misiniz?"
                onConfirm={() => deleteMutation.mutate(record.id)}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Space>
          {isIttihatMirror && (
            <Button
              type="primary"
              icon={<CloudSyncOutlined spin={forceSyncMutation.isPending} />}
              onClick={() => forceSyncMutation.mutate()}
              loading={forceSyncMutation.isPending}
            >
              Hub'dan Şimdi Yenile
            </Button>
          )}
          <Button
            icon={<ImportOutlined />}
            onClick={() => setImportModalOpen(true)}
            title={isIttihatMirror ? 'Acil müdahale (deprecated — Hub mirror authoritative)' : undefined}
          >
            {isIttihatMirror ? 'Manuel İçe Aktar (acil)' : "İttihat'tan İçe Aktar"}
          </Button>
          {!isIttihatMirror && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingSource(null);
                form.resetFields();
                setIsModalOpen(true);
              }}
            >
              Yeni Kaynak
            </Button>
          )}
        </Space>
      </div>

      {isIttihatMirror && (
        <Alert
          type="info"
          showIcon
          icon={<CloudSyncOutlined />}
          style={{ marginBottom: 16 }}
          message="Hub-managed mirror"
          description={
            <span>
              Bu firma <strong>İttihat Hub</strong> backend'i kullanıyor — veri kaynakları hub'ın
              authoritative kataloğundan otomatik sync edilir. Manuel oluşturma/düzenleme/silme
              kapalı; değişiklikler hub'da yapılır.{' '}
              <em>Acil durumda manuel içe aktarım kullanılabilir (deprecated).</em>
            </span>
          }
        />
      )}

      <Table
        columns={columns}
        dataSource={sources}
        rowKey="id"
        loading={isLoading}
        locale={{ emptyText: 'Veri kaynağı bulunamadı' }}
      />

      {/* Create/Edit Modal */}
      <Modal
        title={editingSource ? 'Veri Kaynağını Düzenle' : 'Yeni Veri Kaynağı'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingSource(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Görünen Ad"
            rules={[{ required: true, message: 'Ad gerekli' }]}
          >
            <Input placeholder="Örn: Günlük Satışlar" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="sourceType"
                label="Kaynak Tipi"
                rules={[{ required: true, message: 'Tip seçin' }]}
              >
                <Select
                  options={sourceTypeOptions}
                  placeholder="Tip seçin"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="sourceName"
                label="Veritabanı Nesne Adı"
                rules={[{ required: true, message: 'Nesne adı gerekli' }]}
              >
                <Input placeholder="Örn: dbo.vw_DailySales" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="scheduleInterval"
            label="Otomatik Sync Zamanlaması"
            extra="Boş bırakırsanız sadece manuel sync yapılabilir"
          >
            <Select
              allowClear
              placeholder="Zamanlama seçin (opsiyonel)"
              options={schedulePresets}
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.sourceType !== curr.sourceType}
          >
            {({ getFieldValue }) =>
              getFieldValue('sourceType') === 'sp' && (
                <Form.Item label="Prosedür Parametreleri" extra="Prosedürün beklediği parametre adlarını girin">
                  <Form.List name="parameterList">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }) => (
                          <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                            <Form.Item
                              {...restField}
                              name={[name, 'name']}
                              rules={[{ required: true, message: 'Ad gerekli' }]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input placeholder="Parametre adı (örn: StartDate)" style={{ width: 250 }} />
                            </Form.Item>
                            <Button
                              type="text"
                              danger
                              icon={<MinusCircleOutlined />}
                              onClick={() => remove(name)}
                            />
                          </Space>
                        ))}
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          block
                          icon={<PlusOutlined />}
                        >
                          Parametre Ekle
                        </Button>
                      </>
                    )}
                  </Form.List>
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingSource ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => setIsModalOpen(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Bulk Import Modal */}
      <Modal
        title="İttihat'tan Veri Kaynaklarını İçe Aktar"
        open={importModalOpen}
        onCancel={closeImportModal}
        footer={null}
        width={680}
      >
        <p style={{ color: '#666', marginBottom: 12 }}>
          İttihat ekibinin gönderdiği <code>templates.json</code> dosyasını seçin. Aynı{' '}
          <code>sourceName</code> değerine sahip kayıtlar güncellenir; yenileri eklenir.
        </p>

        <input
          type="file"
          accept="application/json,.json"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleImportFile(f);
          }}
          style={{ marginBottom: 12 }}
        />

        {importFileName && !importParseError && importItems && (
          <div style={{ marginBottom: 12 }}>
            <Tag color="blue">{importFileName}</Tag>
            <span style={{ marginLeft: 8 }}>{importItems.length} kayıt yüklendi</span>
          </div>
        )}

        {importParseError && (
          <div style={{ color: '#ff4d4f', marginBottom: 12 }}>
            JSON okunamadı: {importParseError}
          </div>
        )}

        {importItems && (
          <Table
            size="small"
            pagination={{ pageSize: 5 }}
            dataSource={importItems.slice(0, 50).map((it, i) => ({ ...it, key: i }))}
            columns={[
              { title: 'Ad', dataIndex: 'name', key: 'name' },
              { title: 'sourceName', dataIndex: 'sourceName', key: 'sourceName',
                render: (v: string) => <code>{v}</code> },
              { title: 'Tip', dataIndex: 'sourceType', key: 'sourceType',
                render: (v?: string) => <Tag>{v ?? 'ittihat'}</Tag> },
            ]}
          />
        )}

        {importResult && (
          <div style={{ marginTop: 12, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
            <div>
              <strong>Sonuç:</strong> {importResult.imported} yeni, {importResult.updated} güncellendi
              {importResult.skipped > 0 && `, ${importResult.skipped} atlandı`}
            </div>
            {importResult.errors.length > 0 && (
              <ul style={{ marginTop: 8, marginBottom: 0, fontSize: 12, color: '#ff4d4f' }}>
                {importResult.errors.slice(0, 10).map((er) => (
                  <li key={er.index}>
                    #{er.index} <code>{er.sourceName || '(boş)'}</code> — {er.reason}
                  </li>
                ))}
                {importResult.errors.length > 10 && (
                  <li>… ve {importResult.errors.length - 10} hata daha</li>
                )}
              </ul>
            )}
          </div>
        )}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={closeImportModal}>Kapat</Button>
          <Button
            type="primary"
            disabled={!importItems || importItems.length === 0 || !!importResult}
            loading={importMutation.isPending}
            onClick={() => importItems && importMutation.mutate(importItems)}
          >
            Aktar
          </Button>
        </div>
      </Modal>

      {/* Sync History Drawer */}
      <Drawer
        title="Sync Geçmişi"
        placement="right"
        width={500}
        open={historyDrawerOpen}
        onClose={() => {
          setHistoryDrawerOpen(false);
          setSelectedSourceId(null);
        }}
      >
        {historyLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>Yükleniyor...</div>
        ) : syncHistory.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
            Henüz sync geçmişi bulunmuyor.
          </div>
        ) : (
          <Timeline
            items={syncHistory.map((h: SyncHistory) => ({
              color: h.status === 'completed' ? 'green' : h.status === 'failed' ? 'red' : 'blue',
              dot: h.status === 'completed' ? <CheckCircleOutlined /> :
                   h.status === 'failed' ? <CloseCircleOutlined /> :
                   <ClockCircleOutlined />,
              children: (
                <div>
                  <div style={{ fontWeight: 500 }}>
                    {new Date(h.startedAt).toLocaleString('tr-TR')}
                  </div>
                  <div>
                    <Tag color={h.status === 'completed' ? 'green' : h.status === 'failed' ? 'red' : 'blue'}>
                      {h.status === 'completed' ? 'Başarılı' : h.status === 'failed' ? 'Hata' : h.status}
                    </Tag>
                    {h.status === 'completed' && (
                      <span style={{ marginLeft: 8 }}>{h.rowsFetched} satır</span>
                    )}
                  </div>
                  {h.errorMessage && (
                    <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                      {h.errorMessage}
                    </div>
                  )}
                </div>
              ),
            }))}
          />
        )}
      </Drawer>
    </div>
  );
}
